import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSelfHeal } from "@/hooks/useRealtimeSelfHeal";

export interface DispatchStop {
  id?: string;
  stop_number: number;
  stop_type: "pickup" | "delivery";
  saved_address_id?: string | null;
  name?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  scheduled_date?: string | null;
  scheduled_time_start?: string | null;
  scheduled_time_end?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  reference_number?: string | null;
  pallet_count?: number | null;
  piece_count?: number | null;
  weight_lbs?: number | null;
  instructions?: string | null;
  confidential_notes?: string | null;
}

export interface DispatchRecord {
  id: string;
  direct_load_id: string;
  shipper_id: string;
  awarded_broker_id: string | null;
  status: "draft" | "ready" | "assigned" | "in_transit" | "delivered" | "closed";
  load_pallet_count: number | null;
  load_weight_lbs: number | null;
  load_commodity: string | null;
  confidential_notes: string | null;
  /** Set the first time the shipper saves the dispatch builder. NULL until then. */
  dispatch_prepared_at: string | null;
  dispatch_prepared_by: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/** Load-level stops as captured at posting time (direct_load_stops table). */
export interface LoadStop {
  stop_number: number;
  stop_type: "pickup" | "delivery";
  name?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  scheduled_date?: string | null;
  scheduled_time_start?: string | null;
  scheduled_time_end?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  contact_email?: string | null;
  instructions?: string | null;
  pickup_number?: string | null;
  reference_numbers?: string | null;
  pallet_count?: number | null;
  confidential_notes?: string | null;
}

export interface DispatchPayload {
  exists: boolean;
  is_owner: boolean;
  unlocked?: boolean;
  dispatch?: DispatchRecord;
  stops?: DispatchStop[];
  loadStops?: LoadStop[];
}

export interface UseDirectDispatchOptions {
  /**
   * When true, AUTOMATIC refetches (focus/visibility self-heal, realtime
   * postgres_changes, channel SUBSCRIBED) are skipped. Manual `refresh()`
   * calls always run. Used by the dispatch builder while the form is dirty so
   * a background refetch can never wipe in-progress edits. Read-only
   * broker/shipper views leave this false and keep healing normally.
   */
  suspendRefresh?: boolean;
}

export function useDirectDispatch(loadId: string | undefined, opts: UseDirectDispatchOptions = {}) {
  const { suspendRefresh = false } = opts;
  const [data, setData] = useState<DispatchPayload | null>(null);
  /** True only until the first payload lands — drives the skeleton. */
  const [initialLoading, setInitialLoading] = useState(true);
  /** True during background refetches — form stays mounted. */
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadedOnceRef = useRef(false);
  const suspendRef = useRef(suspendRefresh);
  useEffect(() => { suspendRef.current = suspendRefresh; }, [suspendRefresh]);

  const refresh = useCallback(async () => {
    if (!loadId) {
      setInitialLoading(false);
      return;
    }
    if (loadedOnceRef.current) setRefreshing(true);
    else setInitialLoading(true);
    const [rpcRes, loadStopsRes] = await Promise.all([
      supabase.rpc("get_dispatch_for_user" as any, { p_load_id: loadId }),
      supabase
        .from("direct_load_stops" as any)
        .select("stop_number, stop_type, name, address, city, state, postal_code, scheduled_date, scheduled_time_start, scheduled_time_end, contact_name, contact_phone, contact_email, instructions, pickup_number, reference_numbers, pallet_count, confidential_notes")
        .eq("direct_load_id", loadId)
        .order("stop_number", { ascending: true }),
    ]);
    if (rpcRes.error) setError(rpcRes.error.message);
    const base = (rpcRes.data as any) ?? { exists: false, is_owner: false };
    setData({ ...base, loadStops: (loadStopsRes.data as any[]) ?? [] });
    loadedOnceRef.current = true;
    setInitialLoading(false);
    setRefreshing(false);
  }, [loadId]);

  /** Auto refetch — no-op while suspended (dirty builder form). */
  const autoRefresh = useCallback(() => {
    if (suspendRef.current) return;
    refresh();
  }, [refresh]);

  useEffect(() => {
    loadedOnceRef.current = false;
    refresh();
  }, [refresh]);

  // Realtime: broker's dispatch page relies on this hook's `dispatch_prepared_at`
  // to flip from "shipper is preparing packet" to the full sheet. Subscribe on
  // direct_dispatches filtered by the load, plus child tables when known, so
  // the view updates live without a manual refresh.
  useEffect(() => {
    if (!loadId) return;
    const dispatchId = data?.dispatch?.id;
    let ch = supabase
      .channel(`direct-dispatch-${loadId}-${dispatchId ?? "pending"}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "direct_dispatches", filter: `direct_load_id=eq.${loadId}` },
        () => { autoRefresh(); },
      );
    if (dispatchId) {
      ch = ch
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "direct_dispatch_stops", filter: `dispatch_id=eq.${dispatchId}` },
          () => { autoRefresh(); },
        )
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "direct_dispatch_events", filter: `dispatch_id=eq.${dispatchId}` },
          () => { autoRefresh(); },
        );
    }
    ch.subscribe((status) => {
      console.log(`[useDirectDispatch] channel status: ${status} (load ${loadId})`);
      if (status === "SUBSCRIBED") autoRefresh();
    });
    return () => { supabase.removeChannel(ch); };
  }, [loadId, data?.dispatch?.id, autoRefresh]);

  useRealtimeSelfHeal(autoRefresh, !!loadId);

  return { data, loading: initialLoading, initialLoading, refreshing, error, refresh };
}


/**
 * Create or fetch a dispatch row for a load (owner only).
 */
export async function ensureDispatch(loadId: string, shipperId: string): Promise<string> {
  const { data: existing } = await supabase
    .from("direct_dispatches" as any)
    .select("id")
    .eq("direct_load_id", loadId)
    .maybeSingle();
  if (existing && (existing as any).id) return (existing as any).id;

  const { data: created, error } = await supabase
    .from("direct_dispatches" as any)
    .insert({ direct_load_id: loadId, shipper_id: shipperId, status: "draft" })
    .select("id")
    .single();
  if (error) throw error;
  return (created as any).id;
}

export async function saveDispatch(
  dispatchId: string,
  patch: Partial<DispatchRecord>,
  stops: DispatchStop[],
  actorId?: string,
  counterpartyUserId?: string | null,
  loadId?: string,
  loadNumber?: string | null,
) {
  const nowIso = new Date().toISOString();

  // Update load-level fields.
  // First save by the shipper stamps `dispatch_prepared_at` so the broker
  // stops seeing the "preparing packet" state. Idempotent — never overwritten
  // on subsequent edits (guarded by the WHERE clause below).
  const dispatchPatch: Record<string, any> = {
    load_pallet_count: patch.load_pallet_count ?? null,
    load_weight_lbs: patch.load_weight_lbs ?? null,
    load_commodity: patch.load_commodity ?? null,
    confidential_notes: patch.confidential_notes ?? null,
    status: patch.status ?? undefined,
  };
  if (actorId && patch.status === "ready") {
    dispatchPatch.dispatch_prepared_at = nowIso;
    dispatchPatch.dispatch_prepared_by = actorId;
  }

  const { data: updatedDispatch, error: updErr } = await supabase
    .from("direct_dispatches" as any)
    .update(dispatchPatch)
    .eq("id", dispatchId)
    .select("id");
  if (updErr) throw updErr;
  if (!updatedDispatch || updatedDispatch.length === 0) {
    throw new Error("Dispatch save failed: no dispatch row was updated. You may not have permission.");
  }

  // NOTE: `dispatch_prepared_at` is stamped ONLY by the Mark Ready path above.
  // A plain "Save Draft" must never stamp it — the broker gates visibility on
  // this column, and stamping on first save made work-in-progress drafts
  // visible to the broker.


  // NON-DESTRUCTIVE stop sync — keyed by (stop_number, stop_type).
  // The old implementation deleted every stop row and re-inserted them, which
  // wiped live operational milestone state (status / checked_in_at /
  // completed_at) whenever the shipper re-saved the dispatch packet. We now
  // UPDATE matching rows in place (packet fields only), INSERT new ones, and
  // DELETE only rows that no longer exist in the edited packet.
  const { data: existingStops, error: exStopsErr } = await supabase
    .from("direct_dispatch_stops" as any)
    .select("id, stop_number, stop_type")
    .eq("dispatch_id", dispatchId);
  if (exStopsErr) throw exStopsErr;

  const existingByKey = new Map<string, any>();
  ((existingStops as any[]) ?? []).forEach((r) => existingByKey.set(`${r.stop_type}-${r.stop_number}`, r));

  const packetFields = (s: DispatchStop, stopNumber: number) => ({
    dispatch_id: dispatchId,
    stop_number: stopNumber,
    stop_type: s.stop_type,
    saved_address_id: s.saved_address_id ?? null,
    name: s.name ?? null,
    address: s.address ?? null,
    city: s.city ?? null,
    state: s.state ?? null,
    postal_code: s.postal_code ?? null,
    country: s.country ?? "US",
    latitude: s.latitude ?? null,
    longitude: s.longitude ?? null,
    scheduled_date: s.scheduled_date || null,
    scheduled_time_start: s.scheduled_time_start ?? null,
    scheduled_time_end: s.scheduled_time_end ?? null,
    contact_name: s.contact_name ?? null,
    contact_phone: s.contact_phone ?? null,
    contact_email: s.contact_email ?? null,
    reference_number: s.reference_number ?? null,
    pallet_count: s.pallet_count ?? null,
    piece_count: s.piece_count ?? null,
    weight_lbs: s.weight_lbs ?? null,
    instructions: s.instructions ?? null,
    confidential_notes: s.confidential_notes ?? null,
  });

  const keptKeys = new Set<string>();
  const toInsert: any[] = [];
  for (let i = 0; i < stops.length; i++) {
    const s = stops[i];
    const stopNumber = i + 1;
    const key = `${s.stop_type}-${stopNumber}`;
    keptKeys.add(key);
    const existing = existingByKey.get(key);
    const row = packetFields(s, stopNumber);
    if (existing) {
      // NOTE: status / checked_in_at / completed_at are deliberately absent
      // from the patch so live broker/carrier progress survives packet edits.
      const { data: updRows, error: stopUpdErr } = await supabase
        .from("direct_dispatch_stops" as any)
        .update(row)
        .eq("id", existing.id)
        .select("id");
      if (stopUpdErr) throw stopUpdErr;
      if (!updRows || updRows.length === 0) {
        throw new Error("Dispatch save failed: a stop row could not be updated. You may not have permission.");
      }
    } else {
      toInsert.push(row);
    }
  }

  if (toInsert.length > 0) {
    const { error: insErr } = await supabase
      .from("direct_dispatch_stops" as any)
      .insert(toInsert);
    if (insErr) throw insErr;
  }

  const staleIds = ((existingStops as any[]) ?? [])
    .filter((r) => !keptKeys.has(`${r.stop_type}-${r.stop_number}`))
    .map((r) => r.id);
  if (staleIds.length > 0) {
    const { error: delStopsErr } = await supabase
      .from("direct_dispatch_stops" as any)
      .delete()
      .in("id", staleIds);
    if (delStopsErr) throw delStopsErr;
  }

  // Phase 2 — pickup_scheduled event (idempotent, forward-only).
  // Fires the first time the first pickup stop has both a date and a start time.
  if (actorId) {
    const sorted = [...stops].sort((a, b) => (a.stop_number ?? 0) - (b.stop_number ?? 0));
    const firstPickup = sorted.find((s) => s.stop_type === "pickup");
    if (firstPickup?.scheduled_date && firstPickup?.scheduled_time_start) {
      const { data: existing } = await supabase
        .from("direct_dispatch_events" as any)
        .select("id")
        .eq("dispatch_id", dispatchId)
        .eq("event_type", "pickup_scheduled")
        .limit(1)
        .maybeSingle();
      if (!existing) {
        const { data: eventRows, error: eventErr } = await supabase.from("direct_dispatch_events" as any).insert({
          dispatch_id: dispatchId,
          actor_id: actorId,
          event_type: "pickup_scheduled",
          payload: {
            stop_number: firstPickup.stop_number ?? 1,
            scheduled_date: firstPickup.scheduled_date,
            scheduled_time_start: firstPickup.scheduled_time_start,
            scheduled_time_end: firstPickup.scheduled_time_end ?? null,
          },
        }).select("id");
        if (eventErr) throw eventErr;
        if (!eventRows || eventRows.length === 0) {
          throw new Error("Pickup scheduled event failed: no event row was created.");
        }
        if (counterpartyUserId && loadId) {
          await supabase.from("notifications").insert({
            recipient_user_id: counterpartyUserId,
            type: "dispatch_pickup_scheduled",
            title: "Pickup scheduled",
            message: loadNumber
              ? `Shipper scheduled pickup for load ${loadNumber}`
              : "Shipper scheduled the pickup window",
            action_url: `/lr-direct/dispatches/won/${loadId}`,
            load_id: loadId,
            metadata: { dispatch_id: dispatchId, load_type: "lr_direct" },
          } as any).then(() => {}, () => { /* non-fatal */ });
        }
      }
    }
  }
}
